import * as React from 'react'
import { ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import { Switch, useHistory } from 'react-router-dom'
import { AppAction, AppContext, IBackgroundLocationState } from '../config/appConfig'
import { useDispatch, useSelector } from 'react-redux'
import { Store } from '../reducers'
import { useMount } from '@fluentui/react-hooks'
import { isAppContext } from '../helpers/appHelper'
import { Action, Location } from 'history'
import { Route } from 'react-router'
import Dashboard from './Dashboard'
import Files from './Files'
import Emails from './Emails'
import Parties from './Parties'
import Tasks, { taskBaseUrl } from './Tasks'
import Times, { timeBaseUrl } from './Times'
import Notes from './Notes'
import Settings from './Settings'
import MessageContext from '../context/MessageContext'
import FileContext from '../context/FileContext'
import { extractReferenceNumbers } from '../helpers/referenceNumberHelper'
import useAxios from '../hooks/useAxios'
import { ModuleType } from '../context/GlobalContext'
import useModules from '../hooks/module/useModules'
import { useMollie } from '../hooks/useMollie'
import { useSettings } from '../hooks/useSettings'
import InvoiceApprovals from './InvoiceApprovals'
import { Destination } from '../context/AxiosContext'
import Workflows, { workflowBaseUrl } from './Workflows'
import { isOfficeInitialized } from '../helpers/outlookHelper'
import Reports from './Finance/Reports'
import Invoices from './Invoices'
import Loading from '../components/Loading'
import AuthorizationContext from '../context/AuthorizationContext'

const getPathFromHash = (): string => window.location.hash.substring(1)
const hashIsCorrect = (location: Location): boolean => getPathFromHash() === `#${location.pathname}`

export default function Router(): ReactElement {
    const { loaded: settingsLoaded, settings } = useSettings()
    const axios = useAxios()
    const { outlookMessage } = useContext(MessageContext)
    const { validateReferences } = useContext(FileContext)
    const history = useHistory<IBackgroundLocationState>()
    const { host: currentHost } = useContext(AuthorizationContext)
    const dispatch = useDispatch()
    const currentPage = useSelector<Store, string>(state => state.page)
    const modules = useModules()
    const mollie = useMollie()

    const showDashboard = useMemo(() => settings?.dashboard?.enabled !== false, [settings])
    const [currentLocation, setCurrentLocation] = useState<{ location: Location; action: Action }>(undefined)

    useMount(() => {
        if (!isAppContext([AppContext.Read, AppContext.Compose])) {
            if (window.location.hash?.length > 2) {
                history.push(window.location.hash?.substring(1) || currentPage || '/')
            } else if (currentPage) {
                history.push(currentPage)
            }
        }
    })

    useMount(() => {
        mollie.authorize(window.location)
    })

    useEffect(() => {
        if (
            isOfficeInitialized() &&
            isAppContext(AppContext.Compose) &&
            outlookMessage &&
            (outlookMessage as Office.MessageCompose)?.subject?.getAsync
        ) {
            ;(outlookMessage as Office.MessageCompose)?.subject?.getAsync(subject =>
                validateReferences(extractReferenceNumbers(subject.value)).then(fileIdMap => {
                    if (fileIdMap.size === 1) {
                        const [fileId] = fileIdMap.values()
                        history.push(`/file/${fileId}`)
                    }
                }),
            )
        }
    }, [outlookMessage])

    useEffect(() => {
        const listener = () => {
            // Use the history object, because the useLocation() object is not yet updated when this method is called.
            // This causes issues with preserving the state when showing command dialogs.
            if (!hashIsCorrect(history.location)) {
                history.replace(getPathFromHash(), history.location.state)
            }
        }

        // Make sure that the path in memory gets updated if the hash changes by an external event.
        window.addEventListener('hashchange', listener)

        return () => window.removeEventListener('hashchange', listener)
    }, [])

    useEffect(
        () =>
            history.listen((location: Location, action: Action) => {
                if (['PUSH', 'REPLACE'].includes(action)) {
                    // We only want to keep track if we're not in a mailbox item context (i.e. fullscreen).
                    if (!isAppContext([AppContext.Read, AppContext.Compose])) {
                        // Set a new current page.
                        dispatch({
                            type: AppAction.SetCurrentPage,
                            payload: location.pathname,
                        })
                    }
                }

                setCurrentLocation({ location, action })
            }),
        [],
    )

    useEffect(() => {
        const isFilePath = currentLocation?.location.pathname.match(`.*/file/([^/?\\s]+)`)

        if (isFilePath && ['PUSH'].includes(currentLocation?.action) && currentHost?.RowKey) {
            if (modules.has(ModuleType.AuditTrailTracking)) {
                axios.post(`/audit-trail/${currentHost?.RowKey}`, currentLocation.location.pathname, {
                    baseURL: process.env.API_HOST,
                    headers: {
                        'Content-Type': 'text/plain',
                        SuppressErrors: true,
                        Destination: Destination.Hammock,
                    },
                })
            } else if (!modules.has(ModuleType.AuditTrailTracking)) {
                axios.post('/file/navigate', currentLocation.location.pathname, {
                    headers: { 'Content-Type': 'text/plain', SuppressErrors: true },
                })
            }
        }
    }, [currentLocation])

    // Make sure that the hash location gets updated every time the page changes.
    window.location.hash = `#${history.location.pathname}`

    if (!settings) {
        return <></>
    }

    if (settings?.dashboard?.enabled === false && history?.location?.pathname === '/') {
        history.push('/file')
    }

    if (!settingsLoaded) {
        return <Loading />
    }

    return (
        <Switch location={history.location.state?.background || history.location}>
            {showDashboard && <Route exact path="/" component={Dashboard} />}
            <Route path="/file" component={Files} />
            <Route path="/email" component={Emails} />
            <Route path="/party" component={Parties} />
            <Route path="/note" component={Notes} />
            {modules.has(ModuleType.Tasks) && <Route path={taskBaseUrl} component={Tasks} />}
            {modules.has(ModuleType.Workflow) && <Route path={workflowBaseUrl} component={Workflows} />}
            {modules.has(ModuleType.TimeTracking) && <Route path={timeBaseUrl} component={Times} />}
            {modules.has(ModuleType.InvoiceApproval) && <Route path="/invoice-approval" component={InvoiceApprovals} />}
            {modules.has(ModuleType.Invoices) && <Route path="/invoice" component={Invoices} />}
            {modules.has(ModuleType.Reports) && <Route path="/reports" component={Reports} />}

            <Route path="/settings" component={Settings} />
        </Switch>
    )
}
